import { CC_LOCALE_COOKIE } from "@/lib/cc-request-cookies";
import { DEFAULT_LOCALE, isSiteLocale, localePrefix, type SiteLocale } from "@/lib/i18n/config";

/** One year — matches typical marketing-site locale persistence. */
export const CC_LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function parsePreferredSiteLocale(
  cookieValue: string | undefined | null,
): SiteLocale | null {
  if (!cookieValue) {
    return null;
  }
  const trimmed = cookieValue.trim();
  return isSiteLocale(trimmed) ? trimmed : null;
}

/** Locale used when `/` has no valid saved preference. */
export function resolveRootEntryLocale(
  cookieValue: string | undefined | null,
): SiteLocale {
  return parsePreferredSiteLocale(cookieValue) ?? DEFAULT_LOCALE;
}

export function buildLocaleHomePath(locale: SiteLocale): string {
  return localePrefix(locale) || "/";
}

export type CcLocaleCookieOptions = {
  path: string;
  sameSite: "lax";
  secure: boolean;
  maxAge: number;
};

export function ccLocaleCookieSerializeOptions(
  nodeEnv: string | undefined = process.env.NODE_ENV,
): CcLocaleCookieOptions {
  return {
    path: "/",
    sameSite: "lax",
    secure: nodeEnv === "production",
    maxAge: CC_LOCALE_COOKIE_MAX_AGE_SECONDS,
  };
}

/** Client-side mirror of middleware `cc-locale` persistence (lang switcher). */
export function persistSiteLocalePreference(locale: SiteLocale): void {
  if (typeof document === "undefined") {
    return;
  }

  const { path, sameSite, secure, maxAge } = ccLocaleCookieSerializeOptions();
  const securePart = secure ? "; Secure" : "";
  document.cookie = `${CC_LOCALE_COOKIE}=${encodeURIComponent(locale)}; Path=${path}; Max-Age=${maxAge}; SameSite=${sameSite}${securePart}`;
}
